import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { CheckCircle2, AlertCircle, X } from 'lucide-react';
import clsx from 'clsx';

export type ToastKind = 'info' | 'success' | 'error' | 'warning';
export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: string) => void;
}

export const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  push: (kind, message) =>
    set((s) => ({
      toasts: [...s.toasts, { id: Math.random().toString(36).slice(2), kind, message }],
    })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export function toast(kind: ToastKind, message: string) {
  useToastStore.getState().push(kind, message);
}

export default function ToastViewport() {
  const { toasts, dismiss } = useToastStore();
  return (
    <div className="fixed bottom-4 right-4 flex flex-col gap-2 z-50 w-80">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    setShow(true);
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const Icon =
    toast.kind === 'success'
      ? CheckCircle2
      : toast.kind === 'error' || toast.kind === 'warning'
        ? AlertCircle
        : CheckCircle2;
  const color =
    toast.kind === 'success'
      ? 'text-emerald-600'
      : toast.kind === 'error'
        ? 'text-rose-600'
        : toast.kind === 'warning'
          ? 'text-amber-600'
          : 'text-brand-blue-600';

  return (
    <div
      className={clsx(
        'card flex items-start gap-3 px-4 py-3 transition-all duration-200',
        show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2',
      )}
    >
      <Icon size={18} className={clsx('shrink-0 mt-0.5', color)} />
      <div className="flex-1 text-sm text-slate-700 leading-relaxed">{toast.message}</div>
      <button onClick={onDismiss} className="text-slate-400 hover:text-slate-600">
        <X size={14} />
      </button>
    </div>
  );
}
